/**
 * LocationStep - Step 4 (final) of the Add Plane wizard
 *
 * Allows users to:
 * - Select an area (hangar) from dropdown
 * - Select a unit from the selected area
 * - OR click "Place on Map" to enter placement mode
 */

import { useMemo, useCallback, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { MapPin, ArrowRight, Loader2, Warehouse, Grid3X3, Building2, Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import { useHangarsByLocation } from '@/hooks/useHangars'
import { useUnits } from '@/hooks/useUnits'
import { useMapBuildings } from '@/hooks/useMapBuildings'
import { useLocations } from '@/hooks/useLocations'
import { useAddPlaneWizardStore } from '@/stores/addPlaneWizardStore'
import { calculateUnitCenterPosition } from '@/lib/utils/unit-position'

interface LocationStepProps {
  locationId?: string
  onPlaceOnMap: () => void
  onSubmit: () => void
}

export function LocationStep({
  locationId: locationIdProp,
  onPlaceOnMap,
  onSubmit,
}: LocationStepProps) {
  const navigate = useNavigate()
  const routerLocation = useLocation()

  const {
    locationMethod,
    selectedAreaId,
    selectedUnitId,
    dropdownPosition,
    setLocationMethod,
    setSelectedArea,
    setSelectedUnit,
    setDropdownPosition,
    presenceStatus,
    setPresenceStatus,
    contractData,
    isSubmitting,
    prevStep,
    wizardContext,
    isQuickPlaceMode,
  } = useAddPlaneWizardStore()

  // Auto-default presence based on contract start date
  useEffect(() => {
    if (isQuickPlaceMode) return // Quick-place always defaults to present

    const today = new Date().toISOString().split('T')[0]
    const startDate = contractData.type === 'transient'
      ? contractData.check_in
      : contractData.start_date

    if (startDate && startDate > today) {
      setPresenceStatus('away')
    }
  }, []) // Only on mount — user can override after

  // Resolve effective locationId: prop > wizard context selection
  const effectiveLocationId = locationIdProp || wizardContext.locationId || ''

  // Fetch org locations for the location selector (only when no locationId provided)
  const needsLocationSelector = !locationIdProp && !wizardContext.locationId
  const { data: orgLocations, isLoading: isLoadingLocations } = useLocations()

  // Handle location selection for org-scope pages
  const handleLocationSelect = useCallback((locId: string) => {
    const actualId = locId === 'none' ? '' : locId
    // Update wizard context with selected location
    useAddPlaneWizardStore.setState((state) => ({
      wizardContext: { ...state.wizardContext, locationId: actualId || undefined },
    }))
  }, [])

  // The locationId used for area/unit queries
  const resolvedLocationId = effectiveLocationId

  // Fetch areas (hangars/buildings) for the resolved location
  const { data: areas, isLoading: isLoadingAreas } =
    useHangarsByLocation(resolvedLocationId || undefined)

  // Fetch buildings with 3D data (for position calculation)
  const { buildings } = useMapBuildings(resolvedLocationId || undefined)

  // Fetch units for the selected area
  const { data: units, isLoading: isLoadingUnits } = useUnits(
    selectedAreaId ? { areaId: selectedAreaId } : undefined
  )

  // Filter units to only those belonging to selected area
  const filteredUnits = useMemo(() => {
    if (!units || !selectedAreaId) return []
    return units.filter((unit) => unit.area_id === selectedAreaId)
  }, [units, selectedAreaId])

  // Compute dropdown position when unit is pre-selected but position is missing
  // (e.g. coming from Quick Fit which pre-sets area/unit without position)
  useEffect(() => {
    if (!selectedUnitId || !selectedAreaId || dropdownPosition) return
    const building = buildings.find((b) => b.id === selectedAreaId)
    const unit = filteredUnits.find((u) => u.id === selectedUnitId)
    if (building && unit) {
      const position = calculateUnitCenterPosition(unit, building, building.templateType === 'double_row')
      setDropdownPosition(position)
    }
  }, [selectedUnitId, selectedAreaId, dropdownPosition, buildings, filteredUnits, setDropdownPosition])

  // Get selected area details
  const selectedArea = useMemo(() => {
    if (!selectedAreaId || !areas) return null
    return areas.find((a) => a.id === selectedAreaId)
  }, [areas, selectedAreaId])

  // Get selected unit details
  const selectedUnit = useMemo(() => {
    if (!selectedUnitId || !filteredUnits) return null
    return filteredUnits.find((u) => u.id === selectedUnitId)
  }, [filteredUnits, selectedUnitId])

  // Handle area selection (clears unit and position)
  const handleAreaSelect = (areaId: string) => {
    setSelectedArea(areaId === 'none' ? null : areaId)
    setLocationMethod('dropdown')
    // Clear dropdown position since unit is reset when area changes
    setDropdownPosition(null)
  }

  // Handle unit selection with position calculation
  const handleUnitSelect = (unitId: string) => {
    const selectedUnitIdValue = unitId === 'none' ? null : unitId
    setSelectedUnit(selectedUnitIdValue)
    setLocationMethod('dropdown')

    // Calculate unit center position if unit is selected
    if (selectedUnitIdValue && selectedAreaId) {
      const building = buildings.find((b) => b.id === selectedAreaId)
      const unit = filteredUnits.find((u) => u.id === selectedUnitIdValue)

      if (building && unit) {
        const position = calculateUnitCenterPosition(unit, building, building.templateType === 'double_row')
        setDropdownPosition(position)
      } else {
        setDropdownPosition(null)
      }
    } else {
      setDropdownPosition(null)
    }
  }

  // Determine if we're in non-ramp mode (transient or lease from org-scope page)
  const isNonRampMode = wizardContext.source !== 'ramp'

  // Handle "Place on Map" with navigation for non-ramp mode
  const handlePlaceOnMapClick = useCallback(() => {
    if (isNonRampMode && resolvedLocationId) {
      // Save return path and set placement intent
      useAddPlaneWizardStore.setState((state) => ({
        wizardContext: {
          ...state.wizardContext,
          returnPath: routerLocation.pathname,
        },
        locationMethod: 'placement',
      }))

      // Find location slug from orgLocations for navigation
      const loc = orgLocations?.find((l) => l.id === resolvedLocationId)
      const slug = loc?.location_code?.toLowerCase()
      if (slug) {
        navigate(`/${slug}/ramp`)
      }
    } else {
      onPlaceOnMap()
    }
  }, [isNonRampMode, resolvedLocationId, routerLocation.pathname, orgLocations, navigate, onPlaceOnMap])

  // Check if we can submit
  const canSubmit = locationMethod === 'dropdown'

  // Submit button label based on source
  const getSubmitLabel = () => {
    switch (wizardContext.source) {
      case 'transient': return 'Create Booking'
      case 'lease': return 'Create Lease'
      default: return 'Add Aircraft'
    }
  }

  return (
    <div className="space-y-6">
      {/* Location Selector — shown when no locationId is known (org-scope pages) */}
      {needsLocationSelector && (
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Location *
          </Label>
          <Select
            value={wizardContext.locationId || 'none'}
            onValueChange={handleLocationSelect}
            disabled={isLoadingLocations}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a location..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                <span className="text-muted-foreground">Select a location...</span>
              </SelectItem>
              {orgLocations?.map((loc) => (
                <SelectItem key={loc.id} value={loc.id}>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{loc.name}</span>
                    <span className="text-xs text-muted-foreground">
                      ({loc.location_code})
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Area/Unit Selection — only show when we have a resolved location */}
      {resolvedLocationId && (
        <>
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Warehouse className="w-4 h-4" />
              Area / Hangar {wizardContext.source === 'lease' ? '*' : '(Optional)'}
            </Label>
            <Select
              value={selectedAreaId || 'none'}
              onValueChange={handleAreaSelect}
              disabled={isLoadingAreas}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select an area..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="text-muted-foreground">No specific area</span>
                </SelectItem>
                {isLoadingAreas ? (
                  <div className="flex items-center justify-center py-2 gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading areas...
                  </div>
                ) : (
                  areas?.map((area) => (
                    <SelectItem key={area.id} value={area.id}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{area.name || area.area_code}</span>
                        {area.type && (
                          <span className="text-xs text-muted-foreground">
                            ({area.type})
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Unit Selection (only if area selected) */}
          {selectedAreaId && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Grid3X3 className="w-4 h-4" />
                Unit / Spot {wizardContext.source === 'lease' ? '*' : '(Optional)'}
              </Label>
              <Select
                value={selectedUnitId || 'none'}
                onValueChange={handleUnitSelect}
                disabled={isLoadingUnits || filteredUnits.length === 0}
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={
                      filteredUnits.length === 0
                        ? 'No units available'
                        : 'Select a unit...'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <span className="text-muted-foreground">No specific unit</span>
                  </SelectItem>
                  {isLoadingUnits ? (
                    <div className="flex items-center justify-center py-2 gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading units...
                    </div>
                  ) : (
                    filteredUnits.map((unit) => (
                      <SelectItem key={unit.id} value={unit.id}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {unit.name || unit.unit_code}
                          </span>
                          {unit.type && (
                            <span className="text-xs text-muted-foreground">
                              ({unit.type})
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Selection Summary */}
          {(selectedArea || selectedUnit) && (
            <div className="p-3 bg-muted/50 rounded-lg text-sm">
              <div className="text-muted-foreground mb-1">Selected Location:</div>
              <div className="font-medium">
                {selectedArea?.name || selectedArea?.area_code || 'No area'}
                {selectedUnit && (
                  <>
                    {' → '}
                    {selectedUnit.name || selectedUnit.unit_code}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Presence Status Toggle */}
          <div className="space-y-2">
            <Label>Aircraft Presence</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={presenceStatus === 'present' ? 'default' : 'outline'}
                size="sm"
                className="flex-1 gap-2"
                onClick={() => setPresenceStatus('present')}
              >
                <Eye className="w-4 h-4" />
                Present
              </Button>
              <Button
                type="button"
                variant={presenceStatus === 'away' ? 'default' : 'outline'}
                size="sm"
                className="flex-1 gap-2"
                onClick={() => setPresenceStatus('away')}
              >
                <EyeOff className="w-4 h-4" />
                Away
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {presenceStatus === 'present'
                ? 'Aircraft is physically at this location now.'
                : 'Aircraft has a reserved spot but is not on-site yet.'}
            </p>
          </div>
        </>
      )}

      {/* Divider + Place on Map — only when we have a resolved location */}
      {resolvedLocationId && (
        <>
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                Or place visually
              </span>
            </div>
          </div>

          {/* Place on Map Button */}
          <button
            type="button"
            onClick={handlePlaceOnMapClick}
            className="w-full group relative overflow-hidden rounded-xl bg-gradient-to-r from-primary to-primary/80 p-px focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background"
          >
            <div className="relative flex items-center justify-center gap-3 rounded-xl bg-background px-8 py-4 transition-all group-hover:bg-transparent">
              <MapPin
                className={cn(
                  'text-primary group-hover:text-primary-foreground transition-colors'
                )}
              />
              <span className="font-bold text-foreground group-hover:text-primary-foreground transition-colors">
                Place on Map
              </span>
              <ArrowRight
                className={cn(
                  'text-muted-foreground group-hover:text-primary-foreground group-hover:translate-x-1 transition-all'
                )}
                size={16}
              />
            </div>
          </button>

          <p className="text-xs text-muted-foreground text-center">
            {isNonRampMode
              ? 'Click "Place on Map" to navigate to the ramp view and visually position the aircraft.'
              : 'Click "Place on Map" to visually position the aircraft on the ramp view. The area and unit will be auto-detected.'}
          </p>
        </>
      )}

      {/* Footer Navigation */}
      <div className="flex justify-between items-center pt-4 border-t">
        <Button
          type="button"
          variant="ghost"
          onClick={prevStep}
          disabled={isSubmitting}
        >
          Back
        </Button>

        <Button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit || isSubmitting}
        >
          {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {getSubmitLabel()}
        </Button>
      </div>
    </div>
  )
}

export default LocationStep
